import { ChevronsUpDown, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { SortDirection as SortDirectionEnum } from '@/enums/sort-direction.enum'
import type { TrolleySummary } from '@/hooks/useToolManagement/useGetTrolleySummary'

type SortColumn = Nullable<'name' | 'description' | 'quantity'>
type SortDirection = Nullable<SortDirectionEnum>

interface SummaryTableProps {
  summaries: TrolleySummary[]
  sortColumn: SortColumn
  sortDirection: SortDirection
  onSort: (column: SortColumn) => void
}

function getSortIcon(
  column: SortColumn,
  currentSortColumn: SortColumn,
  sortDirection: SortDirection
) {
  if (currentSortColumn !== column) {
    return <ChevronsUpDown className='h-3 w-3 text-gray-400' />
  }
  if (sortDirection === SortDirectionEnum.Asc) {
    return <ChevronUp className='h-3 w-3 text-gray-600' />
  }
  if (sortDirection === SortDirectionEnum.Desc) {
    return <ChevronDown className='h-3 w-3 text-gray-600' />
  }
  return <ChevronsUpDown className='h-3 w-3 text-gray-400' />
}

export function SummaryTable({
  summaries,
  sortColumn,
  sortDirection,
  onSort,
}: SummaryTableProps) {
  const { t } = useTranslation()

  const safeSummaries = Array.isArray(summaries) ? summaries : []

  return (
    <div>
      <table className='w-full text-sm overflow-x-auto border rounded-lg'>
        <thead className='border-b'>
          <tr>
            <th
              colSpan={3}
              className='px-4 py-3 text-center text-sm font-bold text-[#181D27] uppercase bg-gray-50'
            >
              {t('toolManagement.information')}
            </th>
            <th
              colSpan={3}
              className='px-4 py-3 text-center text-sm font-bold text-[#181D27] uppercase border-l bg-gray-50'
            >
              {t('common.status')}
            </th>
          </tr>
          <tr>
            <th className='px-4 py-3 text-left font-medium text-[#717680]'>
              <div
                className='flex items-center gap-2 cursor-pointer hover:text-gray-900 select-none'
                onClick={() => onSort('name')}
              >
                {t('summarization.trolley', 'Trolley')}
                {getSortIcon('name', sortColumn, sortDirection)}
              </div>
            </th>
            <th className='px-4 py-3 text-left font-medium text-[#717680]'>
              <div
                className='flex items-center gap-2 cursor-pointer hover:text-gray-900 select-none'
                onClick={() => onSort('description')}
              >
                {t('summarization.descriptionColumn', 'Description')}
                {getSortIcon('description', sortColumn, sortDirection)}
              </div>
            </th>
            <th className='px-4 py-3 text-center font-medium text-[#717680]'>
              <div
                className='flex items-center justify-center gap-2 cursor-pointer hover:text-gray-900 select-none'
                onClick={() => onSort('quantity')}
              >
                {t('summarization.toolQuantity', 'Tool Quantity')}
                {getSortIcon('quantity', sortColumn, sortDirection)}
              </div>
            </th>
            <th className='px-4 py-3 text-center font-medium text-green-600 border-l'>
              {t('toolManagement.available')}
            </th>
            <th className='px-4 py-3 text-center font-medium text-orange-600'>
              {t('toolManagement.borrowed')}
            </th>
            <th className='px-4 py-3 text-center font-medium text-red-600'>
              {t('toolManagement.missing')}
            </th>
          </tr>
        </thead>
        <tbody>
          {safeSummaries.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className='px-4 py-8 text-center text-gray-500 text-sm'
              >
                {t('summarization.noTrolleysFound', 'No trolleys found')}
              </td>
            </tr>
          ) : (
            safeSummaries.map((summary: TrolleySummary, index: number) => (
              <tr
                key={summary.id ?? index}
                className={cn(index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
              >
                <td className='px-4 py-3 text-gray-900 font-bold'>
                  {summary.name || '-'}
                </td>
                <td className='px-4 py-3 text-gray-600'>
                  {summary.description || '-'}
                </td>
                <td className='px-4 py-3 text-center text-gray-900'>
                  {summary.quantity || 0}
                </td>
                <td className='px-4 py-3 text-center border-l'>
                  <span
                    className={cn(
                      'inline-block px-2 py-1 text-xs rounded-md',
                      (summary.available_quantity || 0) === 0
                        ? 'text-gray-600 bg-[#F7F8FA]'
                        : 'text-green-700 bg-green-100'
                    )}
                  >
                    {summary.available_quantity || 0}
                  </span>
                </td>
                <td className='px-4 py-3 text-center'>
                  <span
                    className={cn(
                      'inline-block px-2 py-1 text-xs rounded-md',
                      (summary.borrowed_quantity || 0) === 0
                        ? 'text-gray-600 bg-[#F7F8FA]'
                        : 'text-orange-700 bg-orange-100'
                    )}
                  >
                    {summary.borrowed_quantity || 0}
                  </span>
                </td>
                <td className='px-4 py-3 text-center'>
                  <span
                    className={cn(
                      'inline-block px-2 py-1 text-xs rounded-md',
                      (summary.missing_quantity || 0) === 0
                        ? 'text-gray-600 bg-[#F7F8FA]'
                        : 'text-red-700 bg-red-100'
                    )}
                  >
                    {summary.missing_quantity || 0}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
